var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { LogoTechnique } from '@seddi/scene';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { fetchProcessedLogo, StyleLoadError } from '../../api/styleAggregator';
import { setSelectedGraphicId, removeGraphic, addGraphic, replaceGraphicImage, } from '../../store/appSlice';
import { getInitialTransformFromRegion } from './logos/logoPlacement';
import { calculateLogoSizeForRegion, calculateLogoSizeRetainingWidth, findBestAvailableRegionForNewLogo, } from './logos/logoSizingStrategies';
import { CLOUD_CONVERT_API_KEY } from '../../constants/appConstants';
import { fileToImageUrl, needsCloudConvert } from '../../utils/logoConversion';
import { convertRegionName } from '../../utils/regionNameConverter';
const ACCEPTED_FILE_TYPES = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/svg+xml',
    'application/pdf',
    'application/postscript',
    '.ai',
    '.eps',
    '.pdf',
    '.svg',
];
const ACCEPTED_LOGO_FILES = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/webp': ['.webp'],
    'image/svg+xml': ['.svg'],
    'application/pdf': ['.pdf'],
    'application/postscript': ['.eps', '.ai'],
};
const nameWithoutExtension = (filename) => filename.replace(/\.[^/.]+$/, '');
function isHttpsUrl(value) {
    try {
        const url = new URL(value.trim());
        return url.protocol === 'https:';
    }
    catch (_a) {
        return false;
    }
}
export const useGraphicsPanel = ({ disabled = false, editableRegions = [], blockBrandLogoRegions = false, onAddGraphic, onReplaceGraphic, }) => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const logos = useSelector((state) => state.app.mockupLogos);
    const selectedId = useSelector((state) => state.app.selectedGraphicId);
    const styleRegions = useSelector((state) => state.app.styleRegions);
    const isRecipeMode = useSelector((state) => state.app.recipeSource != null);
    const availableRegions = blockBrandLogoRegions ? editableRegions : styleRegions;
    const [isConversionLoading, setIsConversionLoading] = useState(false);
    const [conversionReplacingId, setConversionReplacingId] = useState(null);
    const [isUrlDialogOpen, setIsUrlDialogOpen] = useState(false);
    const [urlDialogMode, setUrlDialogMode] = useState('add');
    const [urlDialogValue, setUrlDialogValue] = useState('');
    const [urlDialogError, setUrlDialogError] = useState(null);
    const [urlDialogTargetId, setUrlDialogTargetId] = useState(null);
    const [isUrlDialogSubmitting, setIsUrlDialogSubmitting] = useState(false);
    const replaceInputRef = useRef(null);
    const replaceTargetIdRef = useRef(null);
    const selectedLogo = useMemo(() => { var _a; return (_a = logos.find(l => l.id === selectedId)) !== null && _a !== void 0 ? _a : null; }, [logos, selectedId]);
    const regionOptions = useMemo(() => {
        if (availableRegions.length > 0) {
            return availableRegions.map((r, i) => ({
                id: i,
                value: r.id,
                label: convertRegionName(r.name) || r.id,
            }));
        }
        const seen = new Set();
        return logos
            .map(l => l.region)
            .filter(r => r && !seen.has(r) && seen.add(r))
            .map((r, i) => ({ id: i, value: r, label: r }));
    }, [availableRegions, logos]);
    const buildLogoFromFile = useCallback((file) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e;
        const { imageSrc, imageSize } = yield fileToImageUrl(file, CLOUD_CONVERT_API_KEY);
        const displayName = nameWithoutExtension(file.name);
        const id = crypto.randomUUID();
        const region = findBestAvailableRegionForNewLogo(availableRegions, logos);
        const size = region ? calculateLogoSizeForRegion(imageSize, region, false) : { x: 5, y: 5 };
        const transform = region
            ? getInitialTransformFromRegion(region, size, false)
            : { translation: { x: 0, y: 0 }, rotation: 0 };
        return {
            id,
            name: file.name,
            displayName,
            imageSrc,
            imageSize,
            region: (_e = (_c = (_a = region === null || region === void 0 ? void 0 : region.id) !== null && _a !== void 0 ? _a : (_b = availableRegions[0]) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : (_d = logos[0]) === null || _d === void 0 ? void 0 : _d.region) !== null && _e !== void 0 ? _e : '',
            size,
            technique: LogoTechnique.Rubber,
            transform,
        };
    }), [availableRegions, logos]);
    const buildLogoFromProcessedUrl = useCallback((processed) => {
        var _a, _b, _c, _d, _e;
        const id = crypto.randomUUID();
        const region = findBestAvailableRegionForNewLogo(availableRegions, logos);
        const size = region
            ? calculateLogoSizeForRegion(processed.imageSize, region, false)
            : { x: 5, y: 5 };
        const transform = region
            ? getInitialTransformFromRegion(region, size, false)
            : { translation: { x: 0, y: 0 }, rotation: 0 };
        return {
            id,
            name: processed.name,
            displayName: processed.displayName,
            imageSrc: processed.imageSrc,
            imageSize: processed.imageSize,
            region: (_e = (_c = (_a = region === null || region === void 0 ? void 0 : region.id) !== null && _a !== void 0 ? _a : (_b = availableRegions[0]) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : (_d = logos[0]) === null || _d === void 0 ? void 0 : _d.region) !== null && _e !== void 0 ? _e : '',
            size,
            sourceUrl: processed.sourceUrl,
            technique: LogoTechnique.Rubber,
            transform,
        };
    }, [availableRegions, logos]);
    const handleSelectLogo = useCallback((id) => {
        dispatch(setSelectedGraphicId(id === selectedId ? null : id));
    }, [dispatch, selectedId]);
    const handleDeselect = useCallback(() => {
        dispatch(setSelectedGraphicId(null));
    }, [dispatch]);
    const processAdd = useCallback((file) => __awaiter(void 0, void 0, void 0, function* () {
        const showLoading = needsCloudConvert(file.name);
        if (showLoading) {
            setIsConversionLoading(true);
        }
        try {
            const logo = yield buildLogoFromFile(file);
            dispatch(addGraphic(logo));
            onAddGraphic === null || onAddGraphic === void 0 ? void 0 : onAddGraphic(file, logo);
        }
        finally {
            if (showLoading) {
                setIsConversionLoading(false);
            }
        }
    }), [buildLogoFromFile, dispatch, onAddGraphic]);
    const onDropAccepted = useCallback((acceptedFiles) => {
        acceptedFiles.forEach(file => processAdd(file));
    }, [processAdd]);
    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        accept: ACCEPTED_LOGO_FILES,
        maxFiles: 1,
        multiple: false,
        onDropAccepted,
        noClick: true,
        useFsAccessApi: false,
        disabled: disabled || isRecipeMode,
    });
    const openUrlDialog = useCallback((mode, targetId = null) => {
        setUrlDialogMode(mode);
        setUrlDialogTargetId(targetId);
        setUrlDialogValue('');
        setUrlDialogError(null);
        setIsUrlDialogOpen(true);
    }, []);
    const handleUrlDialogClose = useCallback(() => {
        if (isUrlDialogSubmitting)
            return;
        setIsUrlDialogOpen(false);
        setUrlDialogValue('');
        setUrlDialogError(null);
        setUrlDialogTargetId(null);
    }, [isUrlDialogSubmitting]);
    const handleUrlDialogValueChange = useCallback((value) => {
        setUrlDialogValue(value);
        setUrlDialogError(null);
    }, []);
    const handleAddClick = useCallback(() => {
        if (isRecipeMode) {
            openUrlDialog('add');
            return;
        }
        open();
    }, [isRecipeMode, open, openUrlDialog]);
    const handleReplaceClick = useCallback((e, logoId) => {
        var _a;
        e.stopPropagation();
        if (isRecipeMode) {
            openUrlDialog('replace', logoId);
            return;
        }
        replaceTargetIdRef.current = logoId;
        (_a = replaceInputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    }, [isRecipeMode, openUrlDialog]);
    const processReplace = useCallback((file, logoId) => __awaiter(void 0, void 0, void 0, function* () {
        const logo = logos.find(l => l.id === logoId);
        if (!logo)
            return;
        setConversionReplacingId(logoId);
        const showLoading = needsCloudConvert(file.name);
        if (showLoading) {
            setIsConversionLoading(true);
        }
        try {
            const { imageSrc, imageSize } = yield fileToImageUrl(file, CLOUD_CONVERT_API_KEY);
            // Size: keep current width, scale height by new image aspect ratio. Transform stays unchanged.
            const nextSize = calculateLogoSizeRetainingWidth(imageSize, logo.size.x);
            dispatch(replaceGraphicImage({
                id: logoId,
                imageSrc,
                imageSize,
                size: nextSize,
            }));
            onReplaceGraphic === null || onReplaceGraphic === void 0 ? void 0 : onReplaceGraphic(file, logoId);
        }
        finally {
            if (showLoading) {
                setIsConversionLoading(false);
            }
            setConversionReplacingId(null);
        }
    }), [dispatch, logos, onReplaceGraphic]);
    const handleReplaceFileChange = useCallback((e) => {
        var _a;
        const file = (_a = e.target.files) === null || _a === void 0 ? void 0 : _a[0];
        const targetId = replaceTargetIdRef.current;
        if (file && targetId)
            processReplace(file, targetId);
        e.target.value = '';
        replaceTargetIdRef.current = null;
    }, [processReplace]);
    const handleUrlDialogSubmit = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const sourceUrl = urlDialogValue.trim();
        if (!isHttpsUrl(sourceUrl)) {
            setUrlDialogError('Enter an https:// image URL.');
            return;
        }
        const apiKey = (_a = searchParams.get('api_key')) !== null && _a !== void 0 ? _a : '';
        if (!apiKey) {
            setUrlDialogError('Missing api_key in the page URL.');
            return;
        }
        setIsUrlDialogSubmitting(true);
        setUrlDialogError(null);
        const isReplace = urlDialogMode === 'replace';
        const targetId = urlDialogTargetId;
        if (isReplace) {
            if (!targetId) {
                setUrlDialogError('No graphic selected to replace.');
                setIsUrlDialogSubmitting(false);
                return;
            }
            setConversionReplacingId(targetId);
        }
        else {
            setIsConversionLoading(true);
        }
        try {
            const processed = yield fetchProcessedLogo({ apiKey, sourceUrl });
            if (isReplace && targetId) {
                const logo = logos.find(l => l.id === targetId);
                if (!logo) {
                    setUrlDialogError('Graphic no longer exists.');
                    return;
                }
                const nextSize = calculateLogoSizeRetainingWidth(processed.imageSize, logo.size.x);
                dispatch(replaceGraphicImage({
                    id: targetId,
                    imageSrc: processed.imageSrc,
                    imageSize: processed.imageSize,
                    size: nextSize,
                    sourceUrl: processed.sourceUrl,
                }));
            }
            else {
                const logo = buildLogoFromProcessedUrl(processed);
                dispatch(addGraphic(logo));
            }
            setIsUrlDialogOpen(false);
            setUrlDialogValue('');
            setUrlDialogTargetId(null);
        }
        catch (error) {
            const message = error instanceof StyleLoadError || error instanceof Error
                ? error.message
                : 'Failed to process logo URL.';
            setUrlDialogError(message);
        }
        finally {
            setIsUrlDialogSubmitting(false);
            setIsConversionLoading(false);
            setConversionReplacingId(null);
        }
    }), [
        buildLogoFromProcessedUrl,
        dispatch,
        logos,
        searchParams,
        urlDialogMode,
        urlDialogTargetId,
        urlDialogValue,
    ]);
    const handleDeleteClick = useCallback((e, logo) => {
        e.stopPropagation();
        dispatch(removeGraphic(logo.id));
    }, [dispatch]);
    return {
        blockBrandLogoRegions,
        ACCEPTED_FILE_TYPES,
        conversionReplacingId,
        getRootProps,
        getInputProps,
        handleAddClick,
        isDragActive,
        handleDeleteClick,
        handleDeselect,
        handleReplaceClick,
        handleReplaceFileChange,
        handleSelectLogo,
        handleUrlDialogClose,
        handleUrlDialogSubmit,
        handleUrlDialogValueChange,
        isConversionLoading,
        isRecipeMode,
        isUrlDialogOpen,
        isUrlDialogSubmitting,
        logos,
        regionOptions,
        replaceInputRef,
        selectedId,
        selectedLogo,
        editableRegions: availableRegions,
        styleRegions,
        urlDialogError,
        urlDialogMode,
        urlDialogValue,
    };
};
